// frontend/src/hooks/useNotifications.js
//
// Polls GET /api/v1/notifications/count every ~30s and exposes the current
// count of OPEN notifications for the signed-in user, so the nav bell can
// show a badge. When the count RISES it:
//   - plays an audible chime (notificationSound.js) — the main way someone
//     who isn't watching the screen finds out; louder/insistent for a
//     'critical' item. Muteable per browser.
//   - fires a desktop notification if the tab is backgrounded and the user
//     granted permission (same mechanism as useUnreadChat.js).
//
// SHORT-POLLING model — this Worker has no WebSockets. 30s (vs chat's 15s)
// is deliberate: a data-integrity finding or a stale room is not as
// time-critical as an incoming message, and the producers only run every
// 15 minutes anyway.
//
// The `active` argument lets a caller pause polling (e.g. while the
// notification panel is open and doing its own list fetch).
import { useEffect, useRef, useState, useCallback } from 'react';
import { apiFetch } from '../api/client';
import { ensureNotificationPermission } from './useUnreadChat';
import { playAlert } from '../lib/notificationSound';

const POLL_MS = 30000;

export { ensureNotificationPermission };

export function useNotifications({ active = true, notify = false, sound = true } = {}) {
  const [count, setCount] = useState(0);
  const [topSeverity, setTopSeverity] = useState(null);
  const prevRef = useRef(0);
  // Skip the alert on the very first poll of a session — a badge that was
  // already at 3 when you logged in shouldn't beep at you.
  const primedRef = useRef(false);
  const timerRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const res = await apiFetch('/notifications/count');
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      if (!data || !data.success) return;
      const next = Number(data.count) || 0;
      const sev = data.top_severity || null;

      const rose = next > prevRef.current;

      if (rose && primedRef.current) {
        if (sound) {
          try { playAlert(sev || 'info'); } catch { /* audio unavailable — ignore */ }
        }
        if (
          notify
          && typeof document !== 'undefined'
          && document.hidden
          && 'Notification' in window
          && Notification.permission === 'granted'
        ) {
          try {
            const n = new Notification('New alert in TALIT PMS', {
              body: `You have ${next} open notification${next === 1 ? '' : 's'}.`,
              tag: 'talit-notifications', // collapses repeats into one toast
            });
            n.onclick = () => { window.focus(); n.close(); };
          } catch {
            /* Notification construction can throw on some platforms — ignore */
          }
        }
      }

      prevRef.current = next;
      primedRef.current = true;
      setCount(next);
      setTopSeverity(sev);
    } catch {
      /* network blip — keep the last known count */
    }
  }, [notify, sound]);

  useEffect(() => {
    if (!active) return undefined;
    poll();
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, poll]);

  // Let a caller force an immediate refresh (e.g. right after acknowledging
  // one from the panel).
  return { count, topSeverity, refresh: poll };
}
